// Display formatting for the court statistics screen (es-AR, voseo copy lives in the components).
//
// Rule that matters: a metric the back could not compute (empty denominator) arrives as `null` and
// must read as a dash. Coercing it to 0 would show "0 % ocupación" for a club that was simply closed.

export const DASH = "—";

const MINUS = "−"; // U+2212, aligns with digits better than a hyphen

const nf0 = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const money = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 });
const dayShort = new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "short", timeZone: "UTC" });
const dayWeekday = new Intl.DateTimeFormat("es-AR", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

const isNum = (n) => typeof n === "number" && Number.isFinite(n);
// Wraps a formatter so non-numbers (null, undefined, NaN) become the dash.
const orDash = (fn) => (n) => (isNum(n) ? fn(n) : DASH);

export const fmtPct = orDash((x) => `${nf1.format(x * 100)} %`);
export const fmtPct0 = orDash((x) => `${nf0.format(x * 100)} %`);
export const fmtInt = orDash((n) => nf0.format(Math.round(n)));
export const fmtNumber1 = orDash((n) => nf1.format(n));
export const fmtMoney = orDash((n) => money.format(Math.round(n)));
export const fmtHours = orDash((h) => `${nf0.format(Math.round(h))} h`);

/** Compact money for tiles and axes: "$ 1,9 M" from a million up, the full amount ("$ 60.000") below. */
export const fmtMoneyCompact = orDash((n) => {
  if (Math.abs(n) >= 1e6) return `$ ${nf1.format(n / 1e6)} M`;
  return money.format(Math.round(n));
});

/** Lead time: hours below a day, days (one decimal) from a day up. */
export const fmtLead = orDash((hours) => (hours < 24 ? `${nf0.format(hours)} h` : `${nf1.format(hours / 24)} días`));

/** "20 a 21 h": the hour slot that starts at `hour`; the slot after 23 ends at 00. */
export const hourRange = (hour) => `${String(hour).padStart(2, "0")} a ${String((hour + 1) % 24).padStart(2, "0")} h`;

// YYYY-MM-DD -> Date at UTC midnight, formatted with timeZone UTC so the browser zone cannot shift it.
const utcDate = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const fmtDayShort = (ymd) => dayShort.format(utcDate(ymd));
export const fmtDayWeekday = (ymd) => capitalize(dayWeekday.format(utcDate(ymd)));
// The year is appended by hand: Intl's `year` option inserts "de" in some ICU versions ("12 de oct de 2026").
const withYear = (ymd) => `${dayShort.format(utcDate(ymd))} ${ymd.slice(0, 4)}`;
export const fmtRange = ({ from, to }) =>
  from === to ? withYear(to) : `${dayShort.format(utcDate(from))} – ${withYear(to)}`;

const signed = (n, format) => (n > 0 ? "+" : n < 0 ? MINUS : "") + format(Math.abs(n));

/**
 * Change of a metric against its comparison period, ready to render.
 *
 * @param {"pct"|"pp"|"lead"} kind  pct = relative change, pp = percentage points between two
 *   ratios, lead = difference of two lead times in hours, shown in days.
 * @param {number|null} current
 * @param {number|null} previous
 * @param {boolean|null} goodUp  true when an increase is good, false when a decrease is good,
 *   null when neither direction is better (the delta is then always neutral).
 * @returns {{text: string, tone: "good"|"bad"|"neutral", arrow: string, word: string}|null}
 *   null when either side is missing, or for a relative change against zero.
 */
export const computeDelta = (kind, current, previous, goodUp) => {
  if (!isNum(current) || !isNum(previous)) return null;

  let diff;
  let text;
  if (kind === "pp") {
    diff = (current - previous) * 100;
    text = `${signed(diff, (x) => nf1.format(x))} pp`;
  } else if (kind === "lead") {
    diff = (current - previous) / 24;
    text = `${signed(diff, (x) => nf1.format(x))} días`;
  } else {
    if (previous === 0) return null;
    diff = (current / previous - 1) * 100;
    text = `${signed(diff, (x) => nf1.format(x))} %`;
  }

  const direction = Math.abs(diff) < 0.05 ? 0 : Math.sign(diff);
  const tone = goodUp == null || direction === 0 ? "neutral" : (direction > 0) === goodUp ? "good" : "bad";
  const arrow = direction > 0 ? "▲" : direction < 0 ? "▼" : "■";
  const word = tone === "good" ? "mejora" : tone === "bad" ? "empeora" : "sin cambio relevante";
  return { text, tone, arrow, word };
};
